/// <amd-module name="Pipeline/Analytics/TaskInsights/ControllerViews/TaskInsight" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { ContextType, IReportContext, ITrendReportConfiguration, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { serviceInstanceId, Telemetry, TaskInsightsDismissalKey } from "Pipeline/Analytics/TaskInsights/Constants";
import * as Resources from "Pipeline/Analytics/TaskInsights/Resources";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { localeFormat } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { TeamFoundationHostType } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Icon } from "VSSUI/Icon";
import { TaskInsightSource } from "../Sources/TaskInsightSource";
import { PipelineInsightsDialog } from "Pipeline/Analytics/PipelineInsights/ControllerView/PipelineInsightsDialog";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Status from "VSSUI/Status";
import { onClickFPS } from "VSS/Platform/FPS";
import { Observer } from "VSSUI/Observer";
import { ObservableValue } from "VSS/Core/Observable";

export interface ITaskInsightProps extends IVssComponentProperties {
    pipelineId: number;
    pipelineRunId: number;
    taskStatus: Status.IStatusProps;
    taskName: string;
    jobId: string;
    phaseId: string;
    stageId: string;
}

export interface ITaskInsightState {
    taskFailureCount?: number;
    totalPipelineRunsCount?: number;
    errorText?: string;
    pipelineReportUrl?: string;
}

export class TaskInsights extends VssComponent<ITaskInsightProps, ITaskInsightState> {
    private _taskInsightSource: TaskInsightSource;
    private _pipelineReportRouteId = "ms.vss-pipelineanalytics-web.pipeline-outcome-route";
    private _telemetryService: IVssTelemetryService;
    private _showInsights = new ObservableValue<boolean>(false);

    constructor(props: ITaskInsightProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};

        if (isFeatureFlagEnabled(context.pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }

        this._taskInsightSource = new TaskInsightSource(this.context.pageContext);
        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.evaluateShowInsights();
        if (this._showInsights.value && !this.state.errorText && this.props.pipelineId && this.props.taskName && this.props.stageId) {
            this.fetchData();
            this.setPipelineReportPageUrl();
        }
    }

    public componentDidUpdate(prevProps: ITaskInsightProps, prevState: ITaskInsightState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.evaluateShowInsights();
        if (prevProps != this.props && this._showInsights.value) {
            this.fetchData();
            this.setPipelineReportPageUrl();
        }
    }

    public render() {
        if (this.state.errorText) {
            this._telemetryService.publishEvent(Telemetry.Area, Telemetry.TaskInsightsOnError, {
                error: this.state.errorText
            });
        }
        if (!this.state.taskFailureCount || !this.state.totalPipelineRunsCount) {
            return null;
        }

        return (
            <>
                {this.validateProps() && this.areInsightsValuable() && (
                    <Observer showInsights={this._showInsights}>
                        {(props: { showInsights: boolean }) => {
                            return props.showInsights ? (
                                <PipelineInsightsDialog
                                    insightsMessage={this.getInsights()}
                                    cardTitle={Resources.DialogTitle}
                                    onViewReportClick={this.onClick}
                                    onDismiss={this.onDismiss}
                                />
                            ) : null;
                        }}
                    </Observer>
                )}
            </>
        );
    }

    private getInsights(): JSX.Element {
        let timesText: string = Resources.TimesText;
        if (this.state.taskFailureCount && this.state.taskFailureCount === 1) {
            timesText = Resources.TimeText;
        }
        const arialabel = localeFormat(Resources.TaskInsightTrendText, this.state.taskFailureCount, timesText, this.state.totalPipelineRunsCount);
        return (
            <div className="flex-row task-insight-container">
                <div className="task-insight-icon">
                    <Icon iconName="CRMCustomerInsightsApp" className="medium" />
                </div>
                <div className="flex-row">
                    <div aria-label={arialabel}>
                        <FormatComponent format={Resources.TaskInsightTrendText}>
                            {this.state.taskFailureCount}
                            {timesText}
                            {this.state.totalPipelineRunsCount}
                        </FormatComponent>
                    </div>
                </div>
            </div>
        );
    }

    private onClick = (): void => {
        onClickFPS(this.context.pageContext, this.state.pipelineReportUrl || "", false);
        this._telemetryService.publishEvent(Telemetry.Area, Telemetry.TaskInsightsViewReport, {
            taskFailureCount: this.state.taskFailureCount,
            totalPipelineRunsCount: this.state.totalPipelineRunsCount
        });
    };

    private async setPipelineReportPageUrl() {
        const tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";
        routeValues["definitionId"] = this.props.pipelineId ? this.props.pipelineId.toString() : "";

        try {
            const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>(
                "IVssContributionService"
            );
            const pipelineOutcomeHubPromise = await contributionService.getContributionAsync("ms.vss-pipelineanalytics-web.pipeline-outcome-hub");

            const pipelineReportUrl = routeUrl(this.context.pageContext, this._pipelineReportRouteId, routeValues);
            this.setState({
                pipelineReportUrl: pipelineReportUrl
            });
        } catch (error) {
            this.onError(error);
        }
    }

    private async fetchData() {
        const reportContext = {
            contextType: ContextType.Build,
            definitionId: this.props.pipelineId
        } as IReportContext;

        const defaultConfValues = {
            trendBy: TrendBy.Days,
            period: Period.Days_14
        } as ITrendReportConfiguration;

        const vssLocationService: IVssLocationService = this.context.pageContext.getService<IVssLocationService>("IVssLocationService");
        await vssLocationService.getServiceLocationAsync(serviceInstanceId, TeamFoundationHostType.ProjectCollection);
        Promise.all([
            this._taskInsightSource.queryTotalPipelineRunsCount(reportContext, defaultConfValues, this.props.taskName),
            this._taskInsightSource.queryTotalTaskFailureCount(
                reportContext,
                defaultConfValues,
                this.props.taskName,
                this.props.jobId,
                this.props.phaseId,
                this.props.stageId
            )
        ])
            .then(values => {
                this.setState({
                    totalPipelineRunsCount: values[0].totalPipelineRunsCount,
                    taskFailureCount: values[1].taskFailureCount
                });
            })
            .catch(error => {
                this.onError(error);
            });
    }

    private onError(error: any) {
        const errorText: string = error.toString();
        this.setState({ errorText: errorText });
    }

    public validateProps() {
        return (
            this.props.taskStatus === Status.Statuses.Failed &&
            this.props.pipelineId != null &&
            this.props.pipelineRunId != null &&
            this.props.taskName != null &&
            this.props.jobId != null &&
            this.props.phaseId != null &&
            this.props.stageId != null
        );
    }

    private areInsightsValuable(): boolean {
        const failureCount = this.state.taskFailureCount;
        if (failureCount && failureCount > 1) {
            return true;
        }
        return false;
    }

    private evaluateShowInsights() {
        const didUserDismissInsightsMessage = window.sessionStorage ? window.sessionStorage.getItem(this.getSessionKey()) : false;
        this._showInsights.value = !didUserDismissInsightsMessage;
    }

    private onDismiss = () => {
        this._showInsights.value = false;
        if (window.sessionStorage) {
            window.sessionStorage.setItem(this.getSessionKey(), "true");
        }
        this._telemetryService.publishEvent(Telemetry.Area, Telemetry.TaskInsightsOnDismiss, {
            taskFailureCount: this.state.taskFailureCount,
            totalPipelineRunsCount: this.state.totalPipelineRunsCount
        });
    };

    private getSessionKey(): string {
        return `${TaskInsightsDismissalKey}/${this.props.pipelineId}/${this.props.pipelineRunId}/${this.props.stageId}/${this.props.phaseId}/${this.props.jobId}/${this.props.taskName}`;
    }
}
Components.add("pipelines-task-insights", TaskInsights);
